import React, { useId, useLayoutEffect, useRef } from 'react'

import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Tooltip from 'components_new/atoms/Tooltip'

// The beam and the gradient shadows are NOT in this list — they're pure
// two-state CSS transitions, so they ease both ways on their own.
const ANIMATED = '.hp-glow, .hp-spin, .hp-pillar, .hp-beam, .hp-shadow'

const SPEED = 3

const s = (base: number) => `${base / SPEED}s`

const styles = `
/* The cycle loops three phases — scan, spin, reveal — and starts/ends at the
   fully revealed logotype, so both activation and the loop wrap are seamless.
   Phases (of the full cycle): morph-in 0–4%, scan 4–43%, spin 43–71.5%,
   reveal 71.5–76%, hold 76–100%. */

/* spin group: stationary during scan, 3 turns during spin (1080° ≡ upright),
   holds upright through the reveal */
@keyframes hp-cycle {
  0%    { transform: rotate(0deg); }
  43%   { transform: rotate(0deg); animation-timing-function: ease-in-out; }
  71.5% { transform: rotate(1080deg); }
  100%  { transform: rotate(1080deg); }
}
/* beam + shadows: morph into the scanner, 2 oscillations, collapsed during
   spin, then grow back to the full beam for the reveal */
@keyframes hp-scan {
  0%          { transform: none; }
  4%          { transform: scaleX(.8) translateX(14%) translateY(0); }
  8.9%        { transform: scaleX(.8) translateX(14%) translateY(-8px); }
  18.6%       { transform: scaleX(.8) translateX(14%) translateY(6px); }
  23.5%       { transform: scaleX(.8) translateX(14%) translateY(0); }
  28.4%       { transform: scaleX(.8) translateX(14%) translateY(-8px); }
  38.1%       { transform: scaleX(.8) translateX(14%) translateY(6px); }
  43%         { transform: scaleX(.8) translateX(14%) translateY(0); }
  44.5%, 71.5% { transform: scaleX(0); }
  76%, 100%   { transform: none; }
}
/* left pillar: fattened through scan + spin, back to normal for the reveal */
@keyframes hp-pillar-cycle {
  0%         { transform: none; }
  4%, 71.5%  { transform: scaleX(2) translateX(25%); }
  76%, 100%  { transform: none; }
}

/* base state — these transitions carry the values home when the animation stops */
.hp-spin   { transform-box: view-box; transform-origin: 26.44px 23.81px; transition: transform ${s(
    0.65
  )} ease-out; }
.hp-pillar { transform-box: fill-box; transform-origin: center; transition: transform ${s(
    0.5
  )} ease; }
.hp-beam   { transform-box: fill-box; transform-origin: center; transition: transform ${s(
    0.5
  )} ease; }
.hp-shadow { transform-box: fill-box; transform-origin: center; transition: transform ${s(
    0.5
  )} ease; }

/* active state — cycle = scan(3s) + spin(2s) + reveal(2s) = s(21) total at SPEED=3 */
.hp-icon.is-active .hp-spin         { animation: hp-cycle ${s(
    21
  )} linear infinite; }
.hp-icon.is-active .hp-beam         { animation: hp-scan ${s(
    21
  )} ease-in-out infinite; }
.hp-icon.is-active .hp-shadow       { animation: hp-scan ${s(
    21
  )} ease-in-out infinite; }
.hp-icon.is-active .hp-pillar.left  { animation: hp-pillar-cycle ${s(
    21
  )} ease-in-out infinite; }

@media (prefers-reduced-motion: reduce) {
  .hp-icon.is-active .hp-spin,
  .hp-icon.is-active .hp-pillar { animation: none; }
  .hp-beam, .hp-shadow { transition: none; }
}
`

export default function ChatAvatar({ active = false }: { active?: boolean }) {
  const id = useId()
  const gradLeft = `hp-grad-left-${id}`
  const gradRight = `hp-grad-right-${id}`
  const rootRef = useRef<HTMLSpanElement>(null)
  const { t } = useTranslation()

  // className stays constant ("hp-icon") so React never rewrites it — the
  // is-active class is managed imperatively below to control exact timing.
  useLayoutEffect(() => {
    const root = rootRef.current

    if (!root) return
    const els = Array.from(root.querySelectorAll<HTMLElement>(ANIMATED))

    if (active) {
      // turn animation on cleanly, with no transition firing on start
      els.forEach((el) => {
        el.style.transition = 'none'
        el.style.transform = ''
        el.style.filter = ''
      })
      void root.offsetWidth
      root.classList.add('is-active')
      requestAnimationFrame(() => {
        els.forEach((el) => (el.style.transition = ''))
      })

      return
    }

    if (!root.classList.contains('is-active')) return

    // 1. freeze the LIVE animated values (read while the animation still runs)
    els.forEach((el) => {
      const cs = getComputedStyle(el)

      el.style.transform = cs.transform
      el.style.filter = cs.filter
    })
    // 2. stop the animation — the frozen inline values now hold the same pose
    root.classList.remove('is-active')
    // 3. commit the frozen state, then ease back to default (transitions fire)
    void root.offsetWidth
    els.forEach((el) => {
      el.style.transform = ''
      el.style.filter = ''
    })
  }, [active])

  return (
    <Tooltip
      placement="right"
      title={`${t('global', 'hi')}! ${t('chat', 'greeting')}`}
    >
      <Box
        ref={rootRef}
        className="hp-icon"
        sx={{
          display: 'inline-flex',
          width: '100%',
          height: '100%',
          [':hover']: {
            transform: 'scale(1.1)'
          }
        }}
      >
        <style>{styles}</style>
        <span style={{ display: 'inline-flex', width: '100%', height: '100%' }}>
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label={active ? 'AI assistant active' : 'AI assistant'}
          >
            <defs>
              <linearGradient
                id={gradLeft}
                x1="18.0258"
                y1="28.9888"
                x2="18.0258"
                y2="31.7939"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#16A34A" />
                <stop offset="1" stopColor="#16A34A" stopOpacity="0" />
              </linearGradient>
              <linearGradient
                id={gradRight}
                x1="34.8568"
                y1="28.9888"
                x2="34.8568"
                y2="31.7939"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#16A34A" />
                <stop offset="1" stopColor="#16A34A" stopOpacity="0" />
              </linearGradient>
            </defs>

            <g className="hp-spin">
              <g className="hp-pillar right">
                <path
                  d="M29.2465 12.5894C29.2465 11.1593 30.4058 10 31.8358 10H37.8777C39.3078 10 40.4671 11.1593 40.4671 12.5894V35.0306C40.4671 36.4607 39.3078 37.62 37.8777 37.62H31.8358C30.4058 37.62 29.2465 36.4607 29.2465 35.0306V12.5894Z"
                  fill="#22C55E"
                />
                <g className="hp-shadow">
                  <path
                    d="M29.2465 28.9888H40.4671V31.7939H29.2465V28.9888Z"
                    fill={`url(#${gradRight})`}
                  />
                </g>
              </g>
              <g className="hp-pillar left">
                <path
                  d="M12.4155 12.5894C12.4155 11.1593 13.5748 10 15.0049 10H21.0468C22.4768 10 23.6362 11.1593 23.6362 12.5894V35.0306C23.6362 36.4607 22.4768 37.62 21.0468 37.62H15.0049C13.5748 37.62 12.4155 36.4607 12.4155 35.0306V12.5894Z"
                  fill="#22C55E"
                />
                <g className="hp-shadow">
                  <path
                    d="M12.4155 28.9888H23.6362V31.7939H12.4155V28.9888Z"
                    fill={`url(#${gradLeft})`}
                  />
                </g>
              </g>
              <g className="hp-beam">
                <path
                  d="M4 21.4364C4 20.0063 5.1593 18.847 6.58937 18.847H40.467V29.2045H6.58938C5.1593 29.2045 4 28.0452 4 26.6152V21.4364Z"
                  fill="#4ADE80"
                />
              </g>
            </g>
          </svg>
        </span>
      </Box>
    </Tooltip>
  )
}
